import { useCfops } from '@/core/domain/invoice/cfop/Cfop.hooks';
import { Cfop } from '@/core/domain/invoice/cfop/Cfop.types';
import { ModalProps } from '@/types/ModalProps';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { CheckOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal } from 'antd';
import { Key, useEffect, useState } from 'react';
import FloatingFormItem from '../FloatingFormItem';
import InfiniteTable from '../InfiniteTable';
import LoadMoreSummary from '../LoadMoreSummary';
import { ColumnsType } from '../Table';

interface CfopSelectModalProps extends ModalProps {
  finishCallback?: (documentTypes?: Cfop[]) => void;
  selectedCfop?: number[];
  onCfopSelect?: (cfop: Cfop) => void;
}

export default function CfopSelectModal({
  visible,
  handleVisible,
  finishCallback,
  selectedCfop,
  onCfopSelect,
}: CfopSelectModalProps) {
  const [form] = Form.useForm();
  const [selectedCfopKeys, setSelectedCfopKeys] = useState<Key[]>();
  const { data, isFetching, hasNextPage, fetchNextPage } = useCfops({
    description: Form.useWatch('description', form),
    code: Form.useWatch('code', form),
  });
  const cfopData = flatInfiniteData(data);

  const handleSelectCfop = (selectedRowKeys: Key[]) => {
    setSelectedCfopKeys(selectedRowKeys);
  };

  const getSelectedCfop = () => {
    const result = cfopData.filter((cur) => selectedCfopKeys?.includes(cur.id));
    return result;
  };

  const handleCancel = (): void => {
    setSelectedCfopKeys([]);

    form.resetFields();

    handleVisible();
  };

  const handleOk = () => {
    const cfops = getSelectedCfop();
    if (finishCallback) {
      finishCallback(cfops);
    }

    handleCancel();
  };

  useEffect(() => {
    if (visible) {
      setSelectedCfopKeys(selectedCfop);
    }
  }, [visible]);

  const columns: ColumnsType<Cfop> = [
    {
      dataIndex: 'description',
      title: 'Descrição',
      width: '90%',
    },
    {
      dataIndex: 'code',
      title: 'CFOP',
      width: '10%',
    },
    onCfopSelect
      ? {
          dataIndex: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button
              type="primary"
              onClick={() => onCfopSelect(row)}
              icon={<CheckOutlined />}
            ></Button>
          ),
          width: '20%',
        }
      : {},
  ];

  return (
    <Modal
      visible={visible}
      onCancel={handleVisible}
      onOk={handleOk}
      title="CFOP"
      width={600}
    >
      <Form layout="inline" form={form}>
        <FloatingFormItem
          name="description"
          label="Descrição"
          style={{ width: '75%', margin: '0 5px' }}
        >
          <Input />
        </FloatingFormItem>
        <FloatingFormItem name="code" label="CFOP" style={{ width: '20%' }}>
          <Input />
        </FloatingFormItem>
      </Form>
      <InfiniteTable
        dataSource={cfopData}
        columns={columns}
        loading={isFetching}
        rowKey={(row) => row.id}
        pagination={false}
        summary={() =>
          hasNextPage ? (
            <LoadMoreSummary
              onClick={fetchNextPage}
              columnsCount={columns.length + 1}
            />
          ) : null
        }
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedCfopKeys,
          onChange: handleSelectCfop,
        }}
      />
    </Modal>
  );
}
